import React from 'react';

import { useBeekeeperContext } from '@lumapps/beekeeper/hooks/useBeekeeperContext';
import { useDispatch, useSelector } from '@lumapps/redux/react';
import { actions, UnreadCountsState } from '@lumapps/unread-count/ducks/unreadCounts';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

/**
 * Hook to manage unread chats counter
 * Loads the counter module asynchronously and subscribes to updates
 */
export const useUnreadChatsCounter = () => {
    const dispatch = useDispatch();
    const unreadCount = useSelector(
        (state: { unreadCounts?: UnreadCountsState }) => state.unreadCounts?.sources?.chats ?? 0,
    );
    const { status, isEnabled } = useBeekeeperContext();

    React.useEffect(() => {
        if (!isEnabled || status !== BaseLoadingStatus.idle) {
            return () => {
                /* no cleanup needed */
            };
        }

        let cleanup: (() => void) | undefined;
        let isCancelled = false;

        const loadCounter = async () => {
            // Load the unread counter module from the microfrontend
            const unreadCounterModule = await import('BeekeeperChatsWebComponents/unreadMessagesCounter');

            const onCounterChanged = unreadCounterModule.default;

            if (isCancelled) {
                return;
            }

            const listenerRef = await onCounterChanged((count: number) => {
                if (!isCancelled) {
                    dispatch(actions.registerUnreadCount({ source: 'chats', count }));
                }
            });

            cleanup = listenerRef.stop;
        };

        // Not waiting for it, it's ok if the count is set only after other components kept loading.
        loadCounter();

        return () => {
            isCancelled = true;
            dispatch(actions.unregisterUnreadCount('chats'));
            if (cleanup) {
                cleanup();
            }
        };
    }, [isEnabled, status, dispatch]);

    return { unreadCount };
};
